/**
 * Formatting Utilities
 *
 * Date, currency, phone, and other formatting utilities.
 */

import { format, formatDistance, formatRelative, isValid, parseISO } from 'date-fns';
import { type CountryCode, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Format a date value
 */
export function formatDate(value: Date | string | number | null | undefined, formatStr = 'MMM d, yyyy'): string {
  if (!value) return '';

  const date = typeof value === 'string' ? parseISO(value) : new Date(value);

  if (!isValid(date)) return '';

  return format(date, formatStr);
}

/**
 * Format a datetime value
 */
export function formatDateTime(
  value: Date | string | number | null | undefined,
  formatStr = 'MMM d, yyyy h:mm a',
): string {
  return formatDate(value, formatStr);
}

/**
 * Format a time value
 */
export function formatTime(value: Date | string | number | null | undefined, formatStr = 'h:mm a'): string {
  return formatDate(value, formatStr);
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(
  value: Date | string | number | null | undefined,
  baseDate: Date = new Date(),
): string {
  if (!value) return '';

  const date = typeof value === 'string' ? parseISO(value) : new Date(value);

  if (!isValid(date)) return '';

  return formatDistance(date, baseDate, { addSuffix: true });
}

/**
 * Format a timestamp to compact relative time (e.g., "Just now", "5m ago", "2h ago", "3d ago").
 * Used by activity timelines for space-constrained display.
 */
export function formatCompactRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

/**
 * Format a timestamp to full date/time (e.g., "Mar 9, 2026, 02:30 PM").
 * Used by activity timelines for tooltip/detail display.
 */
export function formatFullDateTime(timestamp: string): string {
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a date relative to today (e.g., "last Friday at 3:00 PM")
 */
export function formatRelativeDate(
  value: Date | string | number | null | undefined,
  baseDate: Date = new Date(),
): string {
  if (!value) return '';

  const date = typeof value === 'string' ? parseISO(value) : new Date(value);

  if (!isValid(date)) return '';

  return formatRelative(date, baseDate);
}

/**
 * Safely convert a date-like value to an ISO 8601 string. Returns the
 * `fallback` (default `''`) when the value is missing or unparseable.
 *
 * Replaces the unguarded `new Date(x).toISOString()` pattern, which throws
 * `RangeError: Invalid time value` when the input cannot be parsed (pasted
 * garbage in a date input, malformed API timestamp, etc). Always prefer this
 * over the bare call when the source is form state, an inline-edit value,
 * or any other untrusted string.
 *
 * Bare `YYYY-MM-DD` strings (the format `<input type="date">` emits) are
 * interpreted as UTC midnight so the resulting timestamp is timezone-stable
 * regardless of the editor's local timezone — matches the historical
 * behaviour of the per-component `dateInputToIso` helpers we replaced.
 */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function safeToIsoString(value: Date | string | number | null | undefined, fallback = ''): string {
  if (value == null || value === '') return fallback;
  // Pin date-only inputs to UTC midnight for timezone-stable serialization,
  // then route everything through the same parse path.
  const normalized = typeof value === 'string' && DATE_ONLY_RE.test(value) ? `${value}T00:00:00.000Z` : value;
  const date = typeof normalized === 'string' ? parseISO(normalized) : new Date(normalized);
  if (isValid(date)) return date.toISOString();
  // parseISO is strict; try the looser `new Date()` parser for legacy
  // formats it rejects (e.g. slash-separated dates from older data).
  if (typeof normalized === 'string') {
    const looser = new Date(normalized);
    if (isValid(looser)) return looser.toISOString();
  }
  return fallback;
}

// ============================================================================
// Currency Formatting
// ============================================================================

/**
 * Format a currency value
 */
export function formatCurrency(value: number | null | undefined, currency = 'USD', locale = 'en-US'): string {
  if (value == null) return '';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a currency value with no decimal places (whole dollars)
 */
export function formatCurrencyWhole(value: number | null | undefined, currency = 'USD', locale = 'en-US'): string {
  if (value == null) return '';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a currency value from cents
 */
export function formatCurrencyFromCents(cents: number | null | undefined, currency = 'USD', locale = 'en-US'): string {
  if (cents == null) return '';

  return formatCurrency(cents / 100, currency, locale);
}

/**
 * Parse a currency string to number
 */
export function parseCurrency(value: string): number | null {
  if (!value) return null;

  // Remove currency symbols and formatting
  const cleaned = value.replace(/[^0-9.-]/g, '');
  const parsed = parseFloat(cleaned);

  return Number.isNaN(parsed) ? null : parsed;
}

// ============================================================================
// Number Formatting
// ============================================================================

/**
 * Format a number with locale
 */
export function formatNumber(
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions,
  locale = 'en-US',
): string {
  if (value == null) return '';

  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Format a percentage value
 */
export function formatPercentage(value: number | null | undefined, decimalPlaces = 2, locale = 'en-US'): string {
  if (value == null) return '';

  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value / 100);
}

/**
 * Format a rate value as a percentage string (e.g., 8.5 → "8.5%").
 * Unlike formatPercentage, this does NOT divide by 100.
 */
export function formatRate(value: number | null | undefined, decimalPlaces = 1): string {
  if (value == null) return '';

  return `${value.toFixed(decimalPlaces)}%`;
}

/**
 * Format a number with compact notation (e.g., 1.2K, 3.4M)
 */
export function formatCompactNumber(value: number | null | undefined, locale = 'en-US'): string {
  if (value == null) return '';

  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(value);
}

const BYTE_UNITS = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];

/**
 * Format bytes to human-readable size
 */
export function formatBytes(bytes: number | null | undefined, decimals = 2): string {
  if (bytes == null || bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${BYTE_UNITS[i]}`;
}

// ============================================================================
// Phone Formatting
// ============================================================================

/**
 * Format a phone number
 */
export function formatPhone(value: string | null | undefined, defaultCountry: CountryCode = 'US'): string {
  if (!value) return '';

  try {
    const phoneNumber = parsePhoneNumber(value, defaultCountry);
    if (phoneNumber) {
      return phoneNumber.formatInternational();
    }
  } catch {
    // Return original if parsing fails
  }

  return value;
}

/**
 * Format a phone number for display (national format)
 */
export function formatPhoneNational(value: string | null | undefined, defaultCountry: CountryCode = 'US'): string {
  if (!value) return '';

  try {
    const phoneNumber = parsePhoneNumber(value, defaultCountry);
    if (phoneNumber) {
      return phoneNumber.formatNational();
    }
  } catch {
    // Return original if parsing fails
  }

  return value;
}

/**
 * Validate a phone number
 */
export function validatePhone(value: string | null | undefined, defaultCountry: CountryCode = 'US'): boolean {
  if (!value) return false;

  try {
    return isValidPhoneNumber(value, defaultCountry);
  } catch {
    return false;
  }
}

// ============================================================================
// Text Formatting
// ============================================================================

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string | null | undefined, maxLength: number): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;

  return `${text.slice(0, maxLength - 3)}...`;
}

/**
 * Capitalize first letter
 */
export function capitalizeFirst(text: string | null | undefined): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Convert to title case
 */
export function toTitleCase(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Convert camelCase to Title Case
 */
export function camelToTitle(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Convert snake_case to Title Case
 * @deprecated Use snakeToTitleCase from @ops-matrix/shared-types directly
 */
export { snakeToTitleCase as snakeToTitle } from '@ops-matrix/shared-types';

// ============================================================================
// Masking
// ============================================================================

/**
 * Mask a string (e.g., for SSN, credit cards)
 */
export function maskString(value: string | null | undefined, visibleChars = 4, maskChar = '*'): string {
  if (!value) return '';
  if (value.length <= visibleChars) return value;

  const masked = maskChar.repeat(value.length - visibleChars);
  return masked + value.slice(-visibleChars);
}

/**
 * Mask email address
 */
export function maskEmail(email: string | null | undefined): string {
  if (!email) return '';

  const [local, domain] = email.split('@');
  if (!local || !domain) return email;

  const maskedLocal = local.length > 2 ? `${local[0]}${'*'.repeat(local.length - 2)}${local[local.length - 1]}` : local;

  return `${maskedLocal}@${domain}`;
}

/**
 * Format SSN with masking
 */
export function formatSSN(value: string | null | undefined, masked = true): string {
  if (!value) return '';

  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length !== 9) return value;

  if (masked) {
    return `***-**-${cleaned.slice(-4)}`;
  }

  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
}

/**
 * Format EIN
 */
export function formatEIN(value: string | null | undefined): string {
  if (!value) return '';

  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length !== 9) return value;

  return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`;
}
